import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORTED_LANGUAGES = {
  en: { name: 'English', flag: '🇺🇸', dir: 'ltr' },
  es: { name: 'Español', flag: '🇪🇸', dir: 'ltr' },
  fr: { name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  pt: { name: 'Português', flag: '🇧🇷', dir: 'ltr' },
  zh: { name: '中文', flag: '🇨🇳', dir: 'ltr' },
  ar: { name: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  hi: { name: 'हिन्दी', flag: '🇮🇳', dir: 'ltr' },
  de: { name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  et: { name: 'Eesti', flag: '🇪🇪', dir: 'ltr' },
  ja: { name: '日本語', flag: '🇯🇵', dir: 'ltr' },
  ko: { name: '한국어', flag: '🇰🇷', dir: 'ltr' },
  ru: { name: 'Русский', flag: '🇷🇺', dir: 'ltr' },
  it: { name: 'Italiano', flag: '🇮🇹', dir: 'ltr' },
  tr: { name: 'Türkçe', flag: '🇹🇷', dir: 'ltr' },
  nl: { name: 'Nederlands', flag: '🇳🇱', dir: 'ltr' },
  pl: { name: 'Polski', flag: '🇵🇱', dir: 'ltr' },
  sv: { name: 'Svenska', flag: '🇸🇪', dir: 'ltr' },
  th: { name: 'ไทย', flag: '🇹🇭', dir: 'ltr' },
  vi: { name: 'Tiếng Việt', flag: '🇻🇳', dir: 'ltr' },
  id: { name: 'Bahasa', flag: '🇮🇩', dir: 'ltr' }
};

const LanguageContext = createContext();

// Skip translation for these elements
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'SVG', 'PATH', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION', 'NOSCRIPT', 'IFRAME']);
const SKIP_ATTRS = new Set(['data-notranslate', 'data-testid']);

function getTranslationCache(lang) {
  try {
    const raw = localStorage.getItem(`translations_${lang}`);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function setTranslationCache(lang, cache) {
  try {
    const str = JSON.stringify(cache);
    if (str.length < 4 * 1024 * 1024) { // 4MB limit
      localStorage.setItem(`translations_${lang}`, str);
    }
  } catch {}
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState('en');
  const [translating, setTranslating] = useState(false);
  const [version, setVersion] = useState(0);
  const cacheRef = useRef({});
  const batchTimerRef = useRef(null);
  const observerRef = useRef(null);
  const originalTextsRef = useRef(new Map());
  const initializedRef = useRef(false);

  // Auto-detect browser language on mount
  useEffect(() => {
    const saved = localStorage.getItem('app_language');
    let lang = 'en';
    if (saved && SUPPORTED_LANGUAGES[saved]) {
      lang = saved;
    } else {
      // Auto-detect from browser
      const browserLang = navigator.language?.split('-')[0] || 'en';
      lang = SUPPORTED_LANGUAGES[browserLang] ? browserLang : 'en';
      localStorage.setItem('app_language', lang);
    }
    
    if (lang !== 'en') {
      cacheRef.current = getTranslationCache(lang);
      document.documentElement.dir = SUPPORTED_LANGUAGES[lang]?.dir || 'ltr';
    }
    setLanguageState(lang);
    initializedRef.current = true;
  }, []);

  const setLanguage = useCallback((lang) => {
    if (!SUPPORTED_LANGUAGES[lang]) return;
    setLanguageState(lang);
    localStorage.setItem('app_language', lang);
    
    // Restore original texts before switching
    originalTextsRef.current.forEach((original, node) => {
      if (node.parentNode) node.textContent = original;
    });
    originalTextsRef.current.clear();

    if (lang === 'en') {
      cacheRef.current = {};
      setVersion(v => v + 1);
      return;
    }

    cacheRef.current = getTranslationCache(lang);
    document.documentElement.dir = SUPPORTED_LANGUAGES[lang]?.dir || 'ltr';
    
    // Trigger retranslation
    setTimeout(() => translatePage(lang), 100);
  }, []);

  // Collect text nodes from the DOM
  const collectTextNodes = useCallback(() => {
    const nodes = [];
    const walk = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const text = node.textContent?.trim();
          if (!text || text.length < 2 || text.length > 500) return NodeFilter.FILTER_REJECT;
          if (/^[\d\s$%.,/:;()\-+#@!?&=<>{}[\]|\\^~`'"*_]+$/.test(text)) return NodeFilter.FILTER_REJECT;
          
          let parent = node.parentElement;
          while (parent) {
            if (SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
            if (parent.getAttribute('data-notranslate') !== null) return NodeFilter.FILTER_REJECT;
            if (parent.classList?.contains('notranslate')) return NodeFilter.FILTER_REJECT;
            parent = parent.parentElement;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    let node;
    while ((node = walk.nextNode())) nodes.push(node);
    return nodes;
  }, []);

  const translatePage = useCallback(async (lang) => {
    if (!lang || lang === 'en') return;
    
    const textNodes = collectTextNodes();
    const textsToTranslate = [];
    const nodeMap = new Map();

    for (const node of textNodes) {
      const text = node.textContent.trim();
      if (!text) continue;

      // Store original text
      if (!originalTextsRef.current.has(node)) {
        originalTextsRef.current.set(node, node.textContent);
      }

      if (cacheRef.current[text]) {
        // Apply cached translation
        node.textContent = node.textContent.replace(text, cacheRef.current[text]);
      } else {
        textsToTranslate.push(text);
        if (!nodeMap.has(text)) nodeMap.set(text, []);
        nodeMap.get(text).push(node);
      }
    }

    if (textsToTranslate.length === 0) return;

    // Deduplicate
    const unique = [...new Set(textsToTranslate)];
    if (unique.length === 0) return;

    setTranslating(true);

    try {
      // Batch in chunks of 50
      for (let i = 0; i < unique.length; i += 50) {
        const chunk = unique.slice(i, i + 50);
        const res = await axios.post(`${API}/translate/batch`, {
          texts: chunk,
          target_language: lang,
          source_language: 'en'
        });

        const translations = res.data.translations || {};

        for (const [original, translated] of Object.entries(translations)) {
          if (original === translated) continue;
          cacheRef.current[original] = translated;

          // Apply to DOM
          const nodes = nodeMap.get(original) || [];
          for (const n of nodes) {
            if (n.parentNode) {
              const orig = originalTextsRef.current.get(n) || n.textContent;
              n.textContent = orig.replace(original, translated);
            }
          }
        }
      }

      setTranslationCache(lang, cacheRef.current);
    } catch (err) {
      console.error('Translation failed:', err);
    } finally {
      setTranslating(false);
    }
  }, [collectTextNodes]);

  // MutationObserver: Watch for new DOM content and translate it
  useEffect(() => {
    if (!initializedRef.current) return;
    if (language === 'en') {
      if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
      return;
    }

    const debouncedTranslate = () => {
      clearTimeout(batchTimerRef.current);
      batchTimerRef.current = setTimeout(() => translatePage(language), 600);
    };

    observerRef.current = new MutationObserver((mutations) => {
      let hasNewText = false;
      for (const m of mutations) {
        if (m.type === 'childList' && m.addedNodes.length > 0) { hasNewText = true; break; }
        if (m.type === 'characterData') { hasNewText = true; break; }
      }
      if (hasNewText) debouncedTranslate();
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // Initial translation — wait for page to fully render
    const initialDelay = setTimeout(() => translatePage(language), 300);
    // Second pass for lazy-loaded content
    const secondPass = setTimeout(() => translatePage(language), 2000);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      clearTimeout(batchTimerRef.current);
      clearTimeout(initialDelay);
      clearTimeout(secondPass);
    };
  }, [language, translatePage]);

  const value = {
    language,
    setLanguage,
    languages: SUPPORTED_LANGUAGES,
    translating,
    isRTL: SUPPORTED_LANGUAGES[language]?.dir === 'rtl'
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
}

export { SUPPORTED_LANGUAGES };
